///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Loading ring drawn around an existing icon slot (the parent must be
/// `relative`): the indicator for "this layer's picture is incomplete
/// because data is still coming". Motion carries the meaning: moving
/// means data is on its way, still means it isn't, so the ring never
/// animates for errors and reduced-motion users get a static arc with the
/// text label doing the work.
///
/// - downloading / ingesting / frames: three-quarter arc, turning
/// - building (known extent): arc grows with progress
/// - done: full ring fading out (the completion beat)
/// - error: a small static amber dot, no ring
///
/// Cyan is the app's action colour; green stays reserved for now/live and
/// red/amber for alerts, so the ring is cyan everywhere.
///

import type { DisplayedLoadState } from "@/hooks/useLayerLoadStates"
import { cn } from "@/utils/utils"

interface LoadRingProps {
	state: DisplayedLoadState | null
	className?: string
	/** Stroke width in viewBox units of a 32-unit box (1.5 ≈ 1px at 20px) */
	strokeWidth?: number
}

export function LoadRing({ state, className, strokeWidth = 2 }: LoadRingProps) {
	if (!state) return null

	if (state.phase === "error" && !state.done) {
		return (
			<span
				className={cn("pointer-events-none absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-black/60", className)}
				aria-hidden="true"
				data-load-ring="error"
			/>
		)
	}

	const determinate = !state.done && state.phase === "building" && state.progress !== undefined
	// pathLength=100 makes the dash math size-independent
	const dash = state.done ? 100 : determinate ? Math.max(4, Math.round(state.progress! * 100)) : 75
	const spinning = !state.done && !determinate

	return (
		<svg
			viewBox="0 0 32 32"
			className={cn(
				"pointer-events-none absolute -inset-0.5 h-[calc(100%+4px)] w-[calc(100%+4px)] text-cyan-400",
				spinning && "motion-safe:animate-spin",
				state.done ? "opacity-0 transition-opacity duration-300" : "opacity-100",
				className
			)}
			style={spinning ? { animationDuration: "1.4s" } : undefined}
			aria-hidden="true"
			data-load-ring={state.done ? "done" : state.phase}
		>
			<circle
				cx="16" cy="16" r="14.5"
				fill="none"
				stroke="currentColor"
				strokeWidth={strokeWidth}
				strokeLinecap="round"
				pathLength={100}
				strokeDasharray={`${dash} 100`}
				// Start at 12 o'clock for a determinate arc
				transform={determinate ? "rotate(-90 16 16)" : undefined}
				className={determinate ? "transition-[stroke-dasharray] duration-300" : undefined}
			/>
		</svg>
	)
}
